import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

// Use default fonts for now to ensure it works
// Font.register({
//   family: 'Nunito',
//   fonts: [
//     { src: 'https://fonts.gstatic.com/s/nunito/v26/XRXI3I6Li01BKofAjsOUbOvIWzgPDEtj.woff2', fontWeight: 400 },
//   ]
// });

const styles = StyleSheet.create({
  page: { 
    padding: 40, 
    fontSize: 12, 
    fontFamily: 'Helvetica',
    color: '#1F2937'
  },
  header: { 
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#3B82F6',
    paddingBottom: 10
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E40AF',
    marginBottom: 4
  },
  subtitle: { 
    color: '#6B7280', 
    fontSize: 14
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1E40AF',
    marginBottom: 16,
    marginTop: 16
  },
  sectionTitle: { 
    fontSize: 16, 
    fontWeight: 'bold',
    color: '#1E40AF',
    marginTop: 20, 
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 4
  },
  card: { 
    borderWidth: 1, 
    borderColor: '#E5E7EB', 
    borderRadius: 8, 
    padding: 12, 
    marginBottom: 8,
    backgroundColor: '#FEFEFE',
    flexDirection: 'row',
    minPresenceAhead: 20
  },
  cardLeft: {
    flex: 1,
    paddingRight: 8
  },
  cardRight: {
    width: 70,
    height: 70,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  serviceName: { 
    fontSize: 12, 
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4
  },
  serviceText: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 2
  },
  productImage: {
    width: 60,
    height: 60,
    objectFit: 'contain',
    borderRadius: 4
  },
  costSummary: {
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 16,
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    minPresenceAhead: 120
  },
  costTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 12,
    textAlign: 'center'
  },
  costText: {
    fontSize: 12,
    color: '#374151',
    marginBottom: 4
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 8,
    marginBottom: 4
  },
  disclaimerText: {
    textAlign: 'center', 
    color: '#92400E', 
    fontSize: 9,
    marginTop: 20,
    lineHeight: 1.3
  },
  infoText: {
    fontSize: 11,
    color: '#374151',
    marginBottom: 3
  }
});

export type QuoteItem = {
  id: string;
  name: string;
  unitType: string;
  unit: string;
  setupPrice: number;
  recurringPrice: number;
  machines: number;
  units: number;
  image?: string; // path
};

export interface QuotePDFProps {
  locationName: string;
  contact?: { fullName?: string; email?: string; company?: string };
  items: QuoteItem[];
  totals: { setup: number; recurring: number };
  timePeriod?: 4 | 8 | 12;
  logoSrc?: string;
}

export const QuotePDF: React.FC<QuotePDFProps> = ({ locationName, contact, items, totals, timePeriod = 4 }) => {
  const dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const totalCost = totals.setup + totals.recurring;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.companyName}>Bright.Blue</Text>
          <Text style={styles.subtitle}>Commerce and Media Solutions</Text>
        </View>

        {/* Title and Info */}
        <Text style={styles.mainTitle}>Price Estimate</Text>
        <Text style={styles.infoText}>Date: {dateStr}</Text>
        <Text style={styles.infoText}>Location: {locationName}</Text>
        {contact?.fullName && <Text style={styles.infoText}>Contact: {contact.fullName}</Text>}
        {contact?.email && <Text style={styles.infoText}>Email: {contact.email}</Text>}
        {contact?.company && <Text style={styles.infoText}>Company: {contact.company}</Text>}

        {/* Items */}
        <Text style={styles.sectionTitle}>Selected Services</Text>
        {items.map((it) => {
          // For Physical SKU, prices are already tier-specific totals, don't multiply
          const setup = it.id === 'physical-sku' ? it.setupPrice : it.setupPrice * it.units;
          const recurring = it.id === 'physical-sku' ? it.recurringPrice : it.recurringPrice * it.machines * it.units;
          const total = setup + recurring;
          return (
            <View key={it.id} style={styles.card}>
              <View style={styles.cardLeft}>
                <Text style={styles.serviceName}>{it.name}</Text>
                <Text style={styles.serviceText}>Configuration: {it.machines} machines × {it.units} {it.unitType.toLowerCase()}</Text>
                <Text style={styles.serviceText}>Setup Cost: £{setup.toLocaleString('en-GB')}</Text>
                <Text style={styles.serviceText}>{timePeriod} Week Cost: £{recurring.toLocaleString('en-GB')}</Text>
                <Text style={styles.serviceText}>Total: £{total.toLocaleString('en-GB')}</Text>
              </View>
              {it.image && (
                <View style={styles.cardRight}>
                  <Image src={it.image} style={styles.productImage} />
                </View>
              )}
            </View>
          );
        })}

        {/* Cost Summary */}
        <View style={styles.costSummary} wrap={false}>
          <Text style={styles.costTitle}>Investment Summary</Text>
          <Text style={styles.costText}>Total Setup Cost: £{totals.setup.toLocaleString('en-GB')}</Text>
          <Text style={styles.costText}>Total {timePeriod} Week Cost: £{totals.recurring.toLocaleString('en-GB')}</Text>
          <Text style={styles.totalLabel}>Total Cost: £{totalCost.toLocaleString('en-GB')}</Text>
        </View>

        {/* Disclaimer */}
        <Text style={styles.disclaimerText}>
          This estimate is valid for 7 days from the date of issue and is subject to approval by an authorized Bright.Blue representative. Pricing is not binding until an official Bright.Blue quote has been issued and signed off.
        </Text>
      </Page>
    </Document>
  );
}; 